import { Message } from '@twilio/conversations';
import CustomScrollbar from 'app/components/CustomScrollbar';
import Header from 'app/components/Header';
import useFetchLiveChat, { LiveChatItem } from 'app/hooks/fetch/useFetchLiveChat';
import useBackButtonAvailable from 'app/hooks/useBackButtonAvailable';
import useFeatureFlags from 'app/hooks/useFeatureFlags';
import useHTMLLinkClickHandler from 'app/hooks/useHTMLLinkClickHandler';
import useLandingRoute from 'app/hooks/useLandingRoute';
import useOpenOnScroll from 'app/hooks/useOpenOnScroll';
import useOstroWS from 'app/hooks/useOstroWS';
import { isVisibleMessage } from 'app/services/twilio/messages';
import { motion } from 'framer-motion';
import { Fragment, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TailorFooter from 'app/modules/tailor/components/TailorFooter';
import { useTailorHalfOpen } from 'app/hooks/useTailorHalfOpen';
import useLiveChatLinkClickCapture from 'app/hooks/useLiveChatLinkClickCapture';

import ConnectingMessage from '../components/ConnectingMessage';
import LiveChatConsentForm from '../components/LiveChatConsentForm';
import LiveChatHeader from '../components/LiveChatHeader';
import LiveChatPrompt from '../components/LiveChatPrompt';
import MessageFactory from '../components/MessageFactory';
import MessageTypingAnimation from '../components/MessageTypingAnimation';
import TwilioMessageFactory from '../components/TwilioMessageFactory';
import useFetchFormAnswer from '../hooks/mutate/useFetchFormAnswer';
import useFetchFormDismiss from '../hooks/mutate/useFetchFormDismiss';
import useConnectingMessage from '../hooks/useConnectingMessage';
import useConversation from '../hooks/useConversation';
import useInView from '../hooks/useInView';
import useIsTyping from '../hooks/useIsTyping';
import useLiveChatPromptDisabled from '../hooks/useLiveChatPromptDisabled';
import useLiveChatTracking from '../hooks/useLiveChatTracking';
import { useNotificationSound } from '../hooks/useNotificationSound';
import useScrollDown from '../hooks/useScrollDown';
import useTwilioMessages from '../hooks/useTwilioMessages';
import useTwilioUser from '../hooks/useTwilioUser';

const containerVariants = {
  hidden: {
    opacity: 0,
    originX: 0,
    originY: 'top',
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
    },
  },
};

const autoMessageVariants = {
  hidden: {
    opacity: 0,
    originX: 0,
    originY: 'top',
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

export default function LiveChat() {
  const {
    captureChatSessionNurseJoined,
    captureAgentMessageSent,
    captureTermsAccepted,
    captureContactSubmitted,
    captureContactSkipped,
    captureChatScreen,
    captureUserMessageSent,
    captureHalfOpen,
  } = useLiveChatTracking();
  const { data: liveChat, isFetched } = useFetchLiveChat();
  const { mutate: answerForm, isPending: answering } = useFetchFormAnswer();
  const { mutate: dismissForm, isPending: dismissing } = useFetchFormDismiss();
  const {
    data: twilioData,
    hasPreviousPage,
    fetchPreviousPage,
  } = useTwilioMessages(Boolean(liveChat?.twilio_credentials));
  const handleHTMLLinkClick = useHTMLLinkClickHandler();
  const handleLinkClick = useLiveChatLinkClickCapture();
  const disableInput = useLiveChatPromptDisabled();
  const backButtonAvailable = useBackButtonAvailable();
  const connectingMessage = useConnectingMessage();
  const navigate = useNavigate();
  const landingRoute = useLandingRoute();
  const { halfOpen } = useTailorHalfOpen();

  const flags = useFeatureFlags();

  const [newMessage, setNewMessage] = useState('');
  const conversation = useConversation();
  const identity = useTwilioUser()?.identity;
  const isTyping = useIsTyping();
  const { ref: scrollRef, scrollDown, scrollToIndex } = useScrollDown<HTMLDivElement>();
  useOpenOnScroll(scrollRef);
  const chatAvailable = Boolean(liveChat?.twilio_credentials);

  useEffect(() => {
    if (twilioData) scrollDown();
  }, [Boolean(twilioData)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (halfOpen) {
      captureHalfOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useOstroWS();
  useNotificationSound();

  const { ref: inViewRef, inView } = useInView<HTMLDivElement>({
    delayed: true,
  });

  useEffect(() => {
    if (inView && hasPreviousPage) {
      fetchPreviousPage();
    }
  }, [inView, fetchPreviousPage, hasPreviousPage]);

  const mostRecentMessage = useMemo(() => {
    const liveChatMessages = liveChat?.chat_items || [];
    const twilioMessages = twilioData?.pages.flatMap((page) => page.items) || [];
    const allMessages = [...liveChatMessages, ...twilioMessages];
    return allMessages[allMessages.length - 1];
  }, [liveChat, twilioData]);

  useEffect(() => {
    if (!conversation) return;

    const handleParticipantJoined = () => {
      captureChatSessionNurseJoined();
    };

    const handleMessageAdded = (message: Message) => {
      if (!isVisibleMessage(message)) return;
      scrollToIndex(message.index);
      if (message.author === identity) return;

      captureAgentMessageSent();
    };

    conversation.on('messageAdded', handleMessageAdded);
    conversation?.on('participantJoined', handleParticipantJoined);

    return () => {
      conversation?.off('participantJoined', handleParticipantJoined);
      conversation?.off('messageAdded', handleMessageAdded);
    };
  }, [conversation, identity, captureChatSessionNurseJoined, captureAgentMessageSent, scrollToIndex]);

  useLayoutEffect(() => {
    if (chatAvailable) {
      captureChatScreen();
    }
  }, [chatAvailable, captureChatScreen]);

  // scroll to bottom when live chat is fetched
  useEffect(() => {
    if (isFetched && chatAvailable) {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    }
  }, [isFetched, scrollRef, connectingMessage, chatAvailable]);

  // scroll to bottom when new system message is added
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [liveChat?.chat_items, scrollRef]);

  if (!liveChat) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleConsentFormSubmit = (data: any) => {
    if (data) {
      answerForm(data);
      captureTermsAccepted();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = (data: any, form: LiveChatItem) => {
    answerForm(data);

    captureContactSubmitted(form);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSkip = (data: any, form: LiveChatItem) => {
    dismissForm(data);

    captureContactSkipped(form);
  };

  const handleMessageChange = (message: string) => {
    setNewMessage(message);
    conversation?.typing();
  };

  const handleMessageSend = (message: string) => {
    conversation?.sendMessage(message);
    setNewMessage('');

    const length = twilioData?.messages?.length ?? 0;
    captureUserMessageSent(length);
  };

  const handleBackClick = () => {
    navigate(landingRoute, { state: { isBack: true } });
  };

  const showTypingArea = !!chatAvailable;
  const showConsentForm = !chatAvailable && liveChat?.consentMessage && !liveChat?.consentMessage?.completed;

  return (
    <div
      className="flex flex-col grow h-0"
      onClickCapture={handleLinkClick}
    >
      {chatAvailable ? (
        <LiveChatHeader showBackButton={backButtonAvailable} />
      ) : (
        <Header
          onClickBack={handleBackClick}
          showBackButton={backButtonAvailable}
          showExpandButton={halfOpen}
          className="py-5 px-7"
        />
      )}
      <CustomScrollbar
        id="live-chat-scroll-container"
        containerRef={scrollRef}
        className="py-2 px-7"
        alignToBottom={!halfOpen}
      >
        {liveChat.chat_items.map((item, index, array) => (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={item === mostRecentMessage ? autoMessageVariants : {}}
            key={index}
          >
            <MessageFactory
              item={item}
              index={index}
              array={array}
              liveChat={liveChat}
              onClickConsentLink={handleHTMLLinkClick}
              onSubmitMessageForm={handleSubmit}
              onSkipMessageForm={handleSkip}
              isLoadingMessageForm={answering || dismissing}
              prevMessageSource={index > 0 ? array[index - 1].source : ''}
            />
          </motion.div>
        ))}
        <div>
          <div ref={inViewRef} />
          {twilioData?.pages.map((page, index) => (
            <Fragment key={index}>
              {page?.items?.map((message, i, array) => {
                const prevAuthor = array[i - 1]?.author;
                const hideAvatar = !prevAuthor || prevAuthor === message.author;

                return (
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={message === mostRecentMessage ? containerVariants : {}}
                    key={message.index}
                    className="last:mb-6"
                  >
                    <TwilioMessageFactory
                      hideAvatar={hideAvatar || identity === message.author}
                      isAuthor={identity === message.author}
                      message={message}
                      prevMessageAuthor={prevAuthor}
                    />
                  </motion.div>
                );
              })}
            </Fragment>
          ))}
          {isTyping && <MessageTypingAnimation />}
        </div>
      </CustomScrollbar>
      {connectingMessage && liveChat.nurses_are_online ? (
        <div
          id="in-queue-message"
          className="px-7 py-2"
        >
          <ConnectingMessage index={connectingMessage?.index ?? 0} />
        </div>
      ) : null}
      {!halfOpen && (
        <>
          <div className="shrink-0 bg-option">
            {showConsentForm && (
              <div className="p-3">
                <LiveChatConsentForm
                  data={liveChat.consentMessage}
                  isLoading={answering}
                  showCheckbox={liveChat.config.use_toc_pp_checkbox}
                  onSubmit={handleConsentFormSubmit}
                />
              </div>
            )}
            {showTypingArea && (
              <LiveChatPrompt
                message={newMessage}
                onChange={handleMessageChange}
                onSend={handleMessageSend}
                disabled={disableInput}
              />
            )}
          </div>

          {flags.uiShowFooterLinksInLiveChat && <TailorFooter />}
        </>
      )}
    </div>
  );
}
